import KatalLogger, { Level } from '@amzn/katal-logger';
import { fetchAuthSession } from 'aws-amplify/auth';

import { ProvisionedConstants, LOGGING_TIMEOUT_MS } from './constants';

const isDev: boolean = ProvisionedConstants.STAGE === 'Dev';

class AuthenticatedLogger {
  private logger: KatalLogger | null = null;
  private currentToken: string | null = null;
  private initPromise: Promise<KatalLogger> | null = null;

  public getLogger = async (): Promise<KatalLogger> => {
    if (this.logger) {
      return this.logger;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.initializeLogger();
    try {
      const logger = await this.initPromise;
      this.initPromise = null;
      return logger;
    } catch (error) {
      this.initPromise = null;
      throw error;
    }
  };

  private initializeLogger = async (): Promise<KatalLogger> => {
    try {
      const session = await fetchAuthSession();
      const newToken = session.tokens?.idToken?.toString() || null;

      if (this.currentToken !== newToken || !this.logger) {
        this.currentToken = newToken;
        this.logger = new KatalLogger({
          url: ProvisionedConstants.KATAL_LOGGER_URL,
          logThreshold: isDev ? Level.DEBUG : Level.INFO,
          maxLogLineSize: 10000,
          maxLogsPerBatch: 25,
          logToConsole: isDev,
          recordMetrics: false,
          sendToBackendOverride: this.sendToBackend,
        });
        this.logger.addErrorListener();
      }
      return this.logger;
    } catch (error) {
      console.warn('Failed to refresh auth token for logging');
      if (this.logger) {
        return this.logger;
      }
      throw error;
    }
  };

  /**
   * Returns the cached KatalLogger instance synchronously.
   * Used by canonical logs which fire after auth is complete.
   * Avoids the async getLogger() which calls fetchAuthSession() on every invocation.
   */
  public getLoggerSync(): KatalLogger | null {
    return this.logger || null;
  }

  private sendToBackend = async (logData: string): Promise<void> => {
    try {
      if (!this.currentToken) {
        console.warn('No ID token available for logging');
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), LOGGING_TIMEOUT_MS);

      try {
        const response = await fetch(ProvisionedConstants.KATAL_LOGGER_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.currentToken}`,
            'Content-Type': 'application/json',
          },
          body: logData,
          signal: controller.signal,
          keepalive: document.visibilityState === 'hidden',
        });

        if (!response.ok) {
          throw new Error(`Failed to send logs: ${response.status} ${response.statusText}`);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.warn('Failed to send logs:', error);
    }
  };
}

export const authenticatedLogger = new AuthenticatedLogger();
